// War-map lookups the event bindings need, built once from the match setup: cities by SimId, strike origins and
// city adjacency (ownership sweeps run along a city's edges).

import { InvariantViolation, NO_SIM_ID, type CityIndex, type MapCityView, type MatchSetupView, type PlayerId, type SimId } from '@taxwar/net'
import { capitalOf, seatsOf } from '../match/warSetup'

export class WarGeography {
  private readonly bySimId = new Map<number, MapCityView>()
  /** Per seat. */
  private readonly capitals: readonly MapCityView[]
  private readonly adjacency: readonly (readonly CityIndex[])[]

  constructor(readonly setup: MatchSetupView) {
    for (const city of setup.cities) this.bySimId.set(city.simId, city)
    this.capitals = seatsOf(setup).map((seat) => capitalOf(setup, seat))
    const adjacency: CityIndex[][] = setup.cities.map(() => [])
    for (const edge of setup.edges) {
      this.neighbourList(adjacency, edge.a).push(edge.b)
      this.neighbourList(adjacency, edge.b).push(edge.a)
    }
    this.adjacency = adjacency
  }

  city(simId: SimId): MapCityView {
    const city = this.bySimId.get(simId)
    if (city === undefined) throw new InvariantViolation(`event names city ${simId}, which is not on the war map`)
    return city
  }

  cityAt(index: CityIndex): MapCityView {
    const city = this.setup.cities[index]
    if (city === undefined || city.index !== index) throw new InvariantViolation(`war map has no city at index ${index}`)
    return city
  }

  /**
   * Where a strike leaves from: a missile's launch city (its launch doctrine's pick, GDD §7.1), or the issuing
   * capital for immediate strikes, which have no launch city (`site` NO_SIM_ID).
   */
  strikeOrigin(owner: PlayerId, site: SimId): MapCityView {
    if (site !== NO_SIM_ID) return this.city(site)
    const capital = this.capitals[owner]
    if (capital === undefined) throw new InvariantViolation(`strike owner ${owner} is not a belligerent of this war`)
    return capital
  }

  neighbours(index: CityIndex): readonly CityIndex[] {
    const list = this.adjacency[index]
    if (list === undefined) throw new InvariantViolation(`war map has no city at index ${index}`)
    return list
  }

  private neighbourList(adjacency: CityIndex[][], index: CityIndex): CityIndex[] {
    const list = adjacency[index]
    if (list === undefined) throw new InvariantViolation(`war map edge names city index ${index}, which does not exist`)
    return list
  }
}
